// The conversation with Dyia lives in the browser for the session (doc 13 §2): it is not
// stored on the server, and it is gone when the page is reloaded or the user signs out.
import type { AssistantBlock } from '@twinlink/shared';
import { create } from 'zustand';
import type { ActionState, Turn } from './model';

interface DyiaState {
  /** Organization the conversation belongs to; another organization starts a new one. */
  orgId: string | null;
  opened: boolean;
  turns: Turn[];
  setOpened: (opened: boolean) => void;
  enter: (orgId: string) => void;
  /** Adds a turn and returns its id. */
  add: (turn: Omit<Turn, 'id'>) => string;
  update: (turnId: string, change: (turn: Turn) => Partial<Turn>) => void;
  setAction: (turnId: string, index: number, state: ActionState) => void;
  removeBlock: (turnId: string, block: AssistantBlock) => void;
  clear: () => void;
}

let seq = 0;

export const useDyia = create<DyiaState>((set) => ({
  orgId: null,
  opened: false,
  turns: [],
  setOpened: (opened) => set({ opened }),
  enter: (orgId) => set((s) => (s.orgId === orgId ? s : { orgId, turns: [], opened: false })),
  add: (turn) => {
    const id = `t${++seq}`;
    set((s) => ({ turns: [...s.turns, { ...turn, id }] }));
    return id;
  },
  update: (turnId, change) => set((s) => ({ turns: s.turns.map((t) => (t.id === turnId ? { ...t, ...change(t) } : t)) })),
  setAction: (turnId, index, state) => set((s) => ({ turns: s.turns.map((t) => (t.id === turnId ? { ...t, actions: { ...t.actions, [index]: state } } : t)) })),
  // Charts and tables only: a card of changes stays, with what became of it.
  removeBlock: (turnId, block) =>
    set((s) => ({
      turns: s.turns.map((t) => {
        if (t.id !== turnId) return t;
        const at = t.blocks.indexOf(block);
        if (at < 0) return t;
        const actions = Object.fromEntries(Object.entries(t.actions ?? {}).map(([i, a]) => [Number(i) > at ? Number(i) - 1 : Number(i), a]));
        return { ...t, blocks: t.blocks.filter((b) => b !== block), actions };
      }),
    })),
  clear: () => set({ turns: [] }),
}));
